import {Component, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
    ClassificationType,
    IncidentSubtype,
    ParticipantRole,
    ReferenceBookResponse,
    ReferenceBookType,
    TransactionType,
    TypologyApiService,
} from '@npck/af-frontend-api';
import {
    DATE_SERVER_FORMAT,
    download,
    downloadFromBlob,
    getFormattedDateOrNull,
    NESTED_TAB,
    RouterService,
    rxSpinner,
    Spinner,
    TAB,
} from '@npck/libs/commons';
import {SnackbarService} from '@npck/libs/ui-kit';
import {combineLatest} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {AuthHolderService} from '../../../../domain/auth-holder.service';
import {DictionaryHolderService} from '../../../../domain/dictionary-holder.service';
import {CLASSIFICATION_SUBTYPE, CLASSIFICATION_TYPE} from '../../../../domain/query-params.keys';
import {Path} from '../../../../path';
import {AnalyticsTab} from '../../analytics.component';
import {ClassificationTableComponent, TableContext} from './classification-table/classification-table.component';

@Component({
    selector: 'af-classification-details',
    standalone: false,
    templateUrl: './classification-details.component.html',
})
export class ClassificationDetailsComponent {
    @ViewChild(ClassificationTableComponent) transactionTable: ClassificationTableComponent;
    ParticipantRole = ParticipantRole;
    tabPathToTab: Record<string, ClassificationType | AnalyticsTab> = {
        [ClassificationType.FRAUD_TYPE.toLowerCase()]: ClassificationType.FRAUD_TYPE,
        [ClassificationType.TRANSACTION_TYPE.toLowerCase()]: ClassificationType.TRANSACTION_TYPE,
        classification: 'tabClassification',
    };
    title: string;
    tabs: ClassificationDetailsTab[] = ['transactionInformationTab', 'participantInformationTab'];
    selectedTabIndex: number;
    subtype: string;
    activeTab: ClassificationDetailsTab;
    transactionInformationTabTableContext: TableContext;
    type: ClassificationType;
    isExporting: boolean = false;
    Spinner = Spinner;
    private frauds: ReferenceBookResponse[] = [];
    private transactionTypes: ReferenceBookResponse[] = [];

    constructor(
        public authHolderService: AuthHolderService,
        private activatedRoute: ActivatedRoute,
        private typologyApiService: TypologyApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private routerService: RouterService,
        private snackbarService: SnackbarService,
    ) {
        this.type = this.activatedRoute.snapshot.paramMap.get(CLASSIFICATION_TYPE).toUpperCase() as ClassificationType;
        this.subtype = this.activatedRoute.snapshot.paramMap.get(CLASSIFICATION_SUBTYPE).toUpperCase();
        this.activeTab = this.activatedRoute.snapshot.queryParamMap.get(NESTED_TAB) as ClassificationDetailsTab;
        if (this.type === ClassificationType.FRAUD_TYPE) {
            this.tabs.splice(0, 0, 'commonInformationTab');
            this.activeTab = !this.activeTab ? 'commonInformationTab' : this.activeTab;
            this.transactionInformationTabTableContext = 'fraud';
        }
        if (this.type === ClassificationType.TRANSACTION_TYPE) {
            this.activeTab = !this.activeTab ? 'transactionInformationTab' : this.activeTab;
            this.transactionInformationTabTableContext = 'transactions';
        }
        this.selectedTabIndex = this.tabs.findIndex(tab => tab == this.activeTab);
        combineLatest([
            this.dictionaryHolderService.getReference(ReferenceBookType.FRAUD_TYPE),
            this.dictionaryHolderService.getReference(ReferenceBookType.TRANSACTION_TYPE),
        ])
            .pipe(rxSpinner('initial'))
            .subscribe(([frauds, transactionTypes]) => {
                this.handleNavigation(this.selectedTabIndex);
                this.transactionTypes = transactionTypes;
                this.frauds = frauds;
                this.title = this.getTextFromDictionary(this.subtype);
            });
    }

    breadcrumbCustomizer = (path: string): string => {
        return this.getTextFromDictionary(path);
    };

    specialFilter = (path: string): boolean => {
        return this.subtype === path.toUpperCase();
    };

    handleNavigation($event: number): void {
        this.activeTab = this.tabs[$event];
        this.routerService.navigate(
            Path.ANALYTICS_CLASSIFICATIONS_FRAUD_DETAILS,
            {
                pathParams: {
                    [CLASSIFICATION_TYPE]: this.type.toLowerCase(),
                    [CLASSIFICATION_SUBTYPE]: this.subtype.toLowerCase(),
                },
                queryParams: {
                    [TAB]: this.type,
                    [NESTED_TAB]: this.activeTab,
                },
                queryParamsHandling: 'merge',
            });
    }

    downloadDocument(): void {
        download('assets/documents/Руководство_пользователя_типология.pdf');
    }

    downloadExport(): void {
        if (this.isExporting) {
            return;
        }
        this.isExporting = true;
        const ranges = this.transactionTable?.selectedDateRange;
        this.typologyApiService.exportClassificationDetails({
            quarterStartDate: getFormattedDateOrNull(ranges.quarter?.dateFrom, DATE_SERVER_FORMAT),
            quarterEndDate: getFormattedDateOrNull(ranges.quarter?.dateTo, DATE_SERVER_FORMAT),
            monthStartDate: getFormattedDateOrNull(ranges.month?.dateFrom, DATE_SERVER_FORMAT),
            monthEndDate: getFormattedDateOrNull(ranges.month?.dateTo, DATE_SERVER_FORMAT),
            forecastFutureStartDate: getFormattedDateOrNull(ranges.forecastFuture?.dateFrom, DATE_SERVER_FORMAT),
            forecastFutureEndDate: getFormattedDateOrNull(ranges.forecastFuture?.dateTo, DATE_SERVER_FORMAT),
            forecastPastStartDate: getFormattedDateOrNull(ranges.forecastPast?.dateFrom, DATE_SERVER_FORMAT),
            forecastPastEndDate: getFormattedDateOrNull(ranges.forecastPast?.dateTo, DATE_SERVER_FORMAT),
            subtype: this.type === ClassificationType.FRAUD_TYPE ? this.subtype as IncidentSubtype : null,
            transactionType: this.type === ClassificationType.TRANSACTION_TYPE ? this.subtype as TransactionType : null,
        }).pipe(
            rxSpinner('initial'),
            finalize(() => this.isExporting = false),
        ).subscribe({
            next: (file: Blob) => {
                const fileName = `classification_details_by_${this.subtype}.xlsx`;
                downloadFromBlob(file, fileName);
                this.snackbarService.showSnackBar('i18n_af_snackBarMessageFileSuccessfullyExported');
            },
            error: err => {
                throw err;
            },
        });
    }

    editClassification(): void {
        this.routerService.navigate(
            Path.ANALYTICS_CLASSIFICATIONS_FRAUD_DETAILS_EDIT,
            {
                pathParams: {
                    [CLASSIFICATION_TYPE]: this.type.toLowerCase(),
                    [CLASSIFICATION_SUBTYPE]: this.subtype.toLowerCase(),
                },
                queryParams: {
                    [TAB]: this.type,
                    [NESTED_TAB]: this.activeTab,
                },
            });
    }

    private getTextFromDictionary(code: string): string {
        if (this.type.toUpperCase() === ClassificationType.FRAUD_TYPE) {
            return this.frauds?.find(item => item.code === code.toUpperCase())?.name_ru;
        }
        if (this.type.toUpperCase() === ClassificationType.TRANSACTION_TYPE) {
            return this.transactionTypes?.find(item => item.code === code.toUpperCase())?.name_ru;
        }
        return code;
    }
}

export declare type ClassificationDetailsTab =
    'commonInformationTab'
    | 'transactionInformationTab'
    | 'participantInformationTab';
